// assets/process-sticky-scroll.js
(() => {
  const OUTER_SELECTOR = '.process-slider';
  const clamp = (n, min, max) => Math.max(min, Math.min(max, n));
  const isMobile = () => window.matchMedia('(max-width: 889px)').matches;

  // ---------- Video controls ----------
  const initVideos = (outer) => {
    const nodes = outer.querySelectorAll('[data-ps-vid]');
    nodes.forEach((wrap) => {
      const video  = wrap.querySelector('.ps-vid__el');
      const toggle = wrap.querySelector('[data-ps-vid-toggle]');
      if (!video || !toggle) return;

      toggle.setAttribute('aria-pressed', 'false');
      toggle.setAttribute('aria-label', 'Play video');

      const setState = (playing) => {
        toggle.setAttribute('aria-pressed', playing ? 'true' : 'false');
        toggle.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
      };

      const playVideo = async () => {
        try { await video.play(); setState(true); }
        catch { setState(false); }
      };

      const pauseVideo = () => { video.pause(); setState(false); };

      toggle.addEventListener('click', () => {
        if (video.paused) playVideo(); else pauseVideo();
      });

      video.addEventListener('play',  () => setState(true));
      video.addEventListener('pause', () => setState(false));

      wrap._pauseVideo = pauseVideo;
    });
  };

  const pauseVideosExcept = (outer, activeIndex) => {
    const slides = outer.querySelectorAll('.ps-slider-slide');
    slides.forEach((slide, idx) => {
      if (idx === activeIndex) return;
      const wrap = slide.querySelector('[data-ps-vid]');
      if (wrap && typeof wrap._pauseVideo === 'function') wrap._pauseVideo();
    });
  };

  const pauseAllVideos = (outer) => {
    outer.querySelectorAll('[data-ps-vid]').forEach((wrap) => {
      if (typeof wrap._pauseVideo === 'function') wrap._pauseVideo();
    });
  };

  // Lazy load
  const loadVideoSources = (slideEl) => {
    if (!slideEl) return;
    const video = slideEl.querySelector('.ps-vid__el');
    if (!video || video.dataset.loaded === 'true') return;
    slideEl.querySelectorAll('source[data-src]').forEach((s) => {
      s.src = s.dataset.src; s.removeAttribute('data-src');
    });
    try { video.load(); } catch {}
    video.dataset.loaded = 'true';
  };
  const prefetchNextVideo = (slides, idx) => {
    const next = slides[idx + 1];
    if (!next) return;
    const v = next.querySelector('.ps-vid__el');
    if (!v || v.dataset.loaded === 'true') return;
    loadVideoSources(next);
  };

  // ---------- Sticky scroller per instance ----------
  const initSticky = (outer) => {
    const container = outer.querySelector('.process-slider_container');
    const scroller  = container.querySelector('.ps-slider-container');
    const slides    = Array.from(scroller.querySelectorAll('.ps-slider-slide'));
    const mobileUI  = container.querySelector('.mobile-format .ps-mobile-toggle');
    const cta       = container.querySelector('.ps-cta');
    const live      = outer.querySelector('#ps-slide-live');
    if (!slides.length) return;

    // fade-content hooks
    slides.forEach((slide) => {
      slide.querySelectorAll('.slider-textcontent, .ps-vid-container')
        .forEach((el) => el.classList.add('fade-content'));
    });

    initVideos(outer);

    const applyTransformIndex = (i) => {
      scroller.style.transform = `translateX(${-i * 100}%)`;
    };

    // CTA
    let ctaHideTimer;
    const setCTAVisibility = (idx) => {
      const onLast = idx === slides.length - 1;
      if (!cta) return;
      clearTimeout(ctaHideTimer);
      if (onLast) {
        cta.classList.add('ps-cta--visible');
        cta.setAttribute('aria-hidden', 'false');
      } else {
        ctaHideTimer = setTimeout(() => {
          cta.classList.remove('ps-cta--visible');
          cta.setAttribute('aria-hidden', 'true');
        }, 120);
      }
    };

    // Scroll mapping
    const getProgress = () => {
      const rect = outer.getBoundingClientRect();
      const scrollable = rect.height - window.innerHeight;
      return clamp((-rect.top) / (scrollable || 1), 0, 1);
    };
    const getIndexFromProgress = (p) => {
      const total = slides.length;
      return Math.min(total - 1, Math.floor(p * total + 1e-6));
    };

    let lastIndex = -1;
    let rafId = null;

    // Snap governor
    let isSnapping = false;
    let snapCooldown = false;
    const SNAP_COOLDOWN_MS = 450;

    // Helpers
    const setActiveSlideClass = (idx) => {
      slides.forEach((slide, sIdx) => slide.classList.toggle('is-active', sIdx === idx));
    };
    const announce = (idx) => {
      if (!live) return;
      const titleEl = slides[idx].querySelector('.ps-slide-heading');
      const title = titleEl ? titleEl.textContent.trim() : `Slide ${idx + 1}`;
      live.textContent = `Slide ${idx + 1} of ${slides.length}: ${title}`;
    };

    const handleScroll = () => {
      if (isSnapping || snapCooldown) {
        const cur = lastIndex < 0 ? 0 : lastIndex;
        applyTransformIndex(cur);
        return;
      }
      const p = getProgress();
      const raw = getIndexFromProgress(p);
      if (raw === lastIndex) return;

      if (lastIndex >= 0 && Math.abs(raw - lastIndex) > 1) {
        const next = lastIndex + (raw > lastIndex ? 1 : -1);
        snapToIndex(next);
        return;
      }

      applyTransformIndex(raw);
      loadVideoSources(slides[raw]);
      prefetchNextVideo(slides, raw);
      if (container.classList.contains('mode-watch')) pauseVideosExcept(outer, raw);
      else pauseAllVideos(outer);

      setCTAVisibility(raw);
      setActiveSlideClass(raw);
      announce(raw);
      lastIndex = raw;
    };

    const onScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => { rafId = null; handleScroll(); });
    };

    const scrollToSlide = (i) => {
      i = clamp(i, 0, slides.length - 1);
      const targetY = outer.offsetTop + i * window.innerHeight;
      window.scrollTo({ top: targetY, behavior: 'smooth' });

      loadVideoSources(slides[i]);
      prefetchNextVideo(slides, i);
      applyTransformIndex(i);
      if (container.classList.contains('mode-watch')) pauseVideosExcept(outer, i);
      else pauseAllVideos(outer);
      setCTAVisibility(i);
      setActiveSlideClass(i);
      announce(i);
      lastIndex = i;
    };

    const snapToIndex = (i) => {
      isSnapping = true;
      snapCooldown = true;
      scrollToSlide(i);
      setTimeout(() => { isSnapping = false; }, SNAP_COOLDOWN_MS);
      setTimeout(() => { snapCooldown = false; }, SNAP_COOLDOWN_MS);
    };

    // Mobile Read/Watch toggle
    const initMobileToggle = () => {
      if (!mobileUI) return;
      const btns = Array.from(mobileUI.querySelectorAll('[data-mode]'));
      const setMode = (mode) => {
        const isWatch = mode === 'watch';
        container.classList.toggle('mode-watch', isWatch);
        container.classList.toggle('mode-read', !isWatch);
        btns.forEach((b) => {
          const active = b.dataset.mode === mode;
          b.classList.toggle('is-active', active);
          b.setAttribute('aria-selected', active ? 'true' : 'false');
        });
        if (isWatch) {
          const idx = lastIndex < 0 ? 0 : lastIndex;
          loadVideoSources(slides[idx]);
          prefetchNextVideo(slides, idx);
          pauseVideosExcept(outer, idx);
        } else {
          pauseAllVideos(outer);
        }
      };
      setMode('read');
      btns.forEach((b) => b.addEventListener('click', () => setMode(b.dataset.mode)));
    };

    // Keyboard navigation (accessibility)
    const onKey = (e) => {
      const cur = lastIndex < 0 ? 0 : lastIndex;
      let target = cur;
      switch (e.key) {
        case 'ArrowRight':
        case 'PageDown':
          target = clamp(cur + 1, 0, slides.length - 1);
          break;
        case 'ArrowLeft':
        case 'PageUp':
          target = clamp(cur - 1, 0, slides.length - 1);
          break;
        case 'Home':
          target = 0;
          break;
        case 'End':
          target = slides.length - 1;
          break;
        default:
          return;
      }
      e.preventDefault();
      snapToIndex(target);
    };

    // Reset cooldown on new gesture (mobile)
    const resetCooldownOnNewGesture = () => { snapCooldown = false; };
    container.addEventListener('touchstart', resetCooldownOnNewGesture, { passive: true });

    // Listeners + init
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    outer.addEventListener('keydown', onKey); // scope keys to the section

    // Make the slider region focusable so keyboard users can interact
    const sliderRegion = outer.querySelector('.ps-slider');
    if (sliderRegion && !sliderRegion.hasAttribute('tabindex')) sliderRegion.setAttribute('tabindex', '0');

    initMobileToggle();

    // Initial
    loadVideoSources(slides[0]);
    prefetchNextVideo(slides, 0);
    setActiveSlideClass(0);
    announce(0);
    handleScroll();
  };

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll(OUTER_SELECTOR).forEach(initSticky);
  });
})();